import React, { useState } from 'react';
import { ListChecks, ChatTeardropText, CaretRight, CaretLeft, PaperPlaneRight, ChatDots } from '@phosphor-icons/react';
import OperationalStatusBadge from '@/components/OperationalStatusBadge';
import { OPERATIONAL_STATUS } from '@/constants/operationalTruth';

const MissionsChatSidebar = ({ isOpen, onToggle, messages = [], onSendMessage, activeExecution }) => {
  const [activeTab, setActiveTab] = useState('missions');
  const [chatInput, setChatInput] = useState('');

  const isExecuting = activeExecution && !['completed', 'failed', 'timed_out', 'cancelled'].includes(activeExecution.status);

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (!chatInput.trim() || isExecuting) return;
    if (onSendMessage) onSendMessage(chatInput.trim());
    setChatInput('');
  };

  const formatMessageSource = (src) => {
    if (src === 'origin_ai' || src === 'origin') return 'Origin AI';
    if (src === 'production_ai' || src === 'production') return 'Production AI';
    if (src === 'human' || src === 'user') return 'Usuario';
    return src || 'Sistema';
  };

  if (!isOpen) {
    return null;
  }

  return (
    <aside className="h-full w-84 shrink-0 border-l border-[#1F2937] bg-[#0A0C10] text-[#F3F4F6] p-4 flex flex-col transition-all duration-300 min-h-0 overflow-hidden">
      {/* Header & Tabs */}
      <div className="flex items-center justify-between border-b border-[#1F2937] pb-3 mb-3 shrink-0">
        <div className="flex items-center bg-[#13131A] p-1 border border-[#1F2937]">
          <button
            onClick={() => setActiveTab('missions')}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono font-semibold transition-all ${
              activeTab === 'missions'
                ? 'bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/40 shadow'
                : 'text-[#9CA3AF] hover:text-[#F3F4F6]'
            }`}
          >
            <ListChecks className="w-3.5 h-3.5" /> Misiones
          </button>
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono font-semibold transition-all ${
              activeTab === 'chat'
                ? 'bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/40 shadow'
                : 'text-[#9CA3AF] hover:text-[#F3F4F6]'
            }`}
          >
            <ChatTeardropText className="w-3.5 h-3.5" /> Chat de Sesión
          </button>
        </div>

        <button
          onClick={onToggle}
          className="p-1 hover:bg-[#13131A] text-[#9CA3AF] hover:text-[#00E5FF] rounded transition-colors"
          title="Colapsar Panel"
        >
          <CaretRight className="w-4 h-4" />
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden text-xs">
        {activeTab === 'missions' ? (
          <div className="flex-1 overflow-y-auto space-y-3 pr-1 min-h-0">
            <div className="p-2 bg-[#13131A] border border-[#1F2937] flex items-center justify-between shrink-0">
              <span className="text-[11px] font-mono text-[#9CA3AF]">Misiones (Futuro)</span>
              <OperationalStatusBadge status={OPERATIONAL_STATUS.STATIC} label="STATIC / SAMPLE" />
            </div>

            <div className="p-3 bg-[#13131A] border border-[#1F2937] space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-mono font-bold text-[#F3F4F6] text-xs">Misión #1: Diagnóstico de Conectores</span>
                <OperationalStatusBadge status={OPERATIONAL_STATUS.STATIC} size="sm" />
              </div>
              <p className="text-[#9CA3AF] text-[11px] leading-relaxed">
                Ejemplo estático: Verificación visual del canal entre Origin AI y Production AI.
              </p>
            </div>

            <div className="p-3 bg-[#13131A] border border-[#1F2937] space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-mono font-bold text-[#F3F4F6] text-xs">Misión #2: Traspaso de Contexto</span>
                <OperationalStatusBadge status={OPERATIONAL_STATUS.STATIC} size="sm" />
              </div>
              <p className="text-[#9CA3AF] text-[11px] leading-relaxed">
                Ejemplo estático: Generación demostrativa del paquete de interoperabilidad.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="p-2 bg-[#13131A] border border-[#1F2937] flex items-center justify-between shrink-0 mb-2">
              <span className="text-[11px] font-mono text-[#9CA3AF]">Chat de Sesión</span>
              <OperationalStatusBadge status={OPERATIONAL_STATUS.REAL} label="REAL / SESSION MESSAGES" />
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-0 mb-2">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-[#9CA3AF] font-mono text-xs">
                  <ChatDots size={24} className="mx-auto mb-2 text-[#4B5563]" />
                  <p>No hay mensajes en esta sesión todavía.</p>
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const src = msg.source || 'system';
                  const isOrigin = src === 'origin_ai' || src === 'origin';
                  const isProd = src === 'production_ai' || src === 'production';
                  const isHuman = src === 'human' || src === 'user';

                  return (
                    <div
                      key={msg.id || idx}
                      className={`p-2.5 text-xs leading-relaxed font-mono border rounded ${
                        isOrigin
                          ? 'bg-[#13131A] border-[#D97706]/40 text-[#D97706]'
                          : isProd
                          ? 'bg-[#13131A] border-[#00E5FF]/40 text-[#00E5FF]'
                          : isHuman
                          ? 'bg-[#13131A] border-[#10B981]/40 text-[#10B981]'
                          : 'bg-[#13131A] border-[#1F2937] text-[#F3F4F6]'
                      }`}
                    >
                      <div className="font-bold text-[10px] opacity-80 uppercase tracking-wider mb-1 flex items-center justify-between">
                        <span>{formatMessageSource(src)}</span>
                        {msg.timestamp && (
                          <span className="text-[10px] text-[#6B7280] font-normal lowercase">
                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                      <div className="text-[#F3F4F6] whitespace-pre-wrap">{msg.content || msg.text}</div>
                    </div>
                  );
                })
              )}
            </div>

            <form onSubmit={handleChatSubmit} className="flex gap-2 pt-2 border-t border-[#1F2937] shrink-0">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder={isExecuting ? "Ejecutando..." : "Enviar mensaje a la sesión..."}
                disabled={isExecuting}
                className="flex-1 bg-[#0A0C10] text-[#F3F4F6] px-2.5 py-1.5 border border-[#1F2937] text-xs font-mono focus:outline-none focus:border-[#00E5FF] disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={isExecuting}
                className="px-3 py-1.5 bg-[#00E5FF] text-[#0A0C10] font-mono font-bold text-xs hover:bg-[#00E5FF]/90 transition-colors flex items-center justify-center disabled:opacity-50"
              >
                <PaperPlaneRight className="w-3.5 h-3.5 font-bold" />
              </button>
            </form>
          </div>
        )}
      </div>
    </aside>
  );
};

export default MissionsChatSidebar;
